/**
 * Shopify Variant Filter Script
 *
 * @license MIT
 * @author Ola Nygards
 * @version 1.0.0
 *
 * This script powers a dynamic, client-side filtering system for Shopify 2.0 themes.
 * It listens for changes on filter checkboxes (e.g., size, brand), fetches
 * product data using the Storefront GraphQL API, and re-renders the product grid.
 *
 * Key Features:
 * - Filters products by variants (e.g., show only products with "Medium" in stock).
 * - Dynamically updates the product grid without page reloads.
 * - Constructs GraphQL queries based on selected filters.
 * - Placeholder for rendering logic.
 */

class VariantFilter {
  constructor() {
    this.filterWrapper = document.querySelector('.variant-filter-wrapper');
    if (!this.filterWrapper) {
      return;
    }

    this.collectionId = `gid://shopify/Collection/${this.filterWrapper.dataset.collectionId}`;
    this.checkboxes = this.filterWrapper.querySelectorAll('input[type="checkbox"]');

    this.productGrid = document.getElementById('product-grid');
    this.productGridContainer = document.querySelector('.collection-products');

    this.initialProducts = []; // Cache initial products to derive filters
    this.productCardMap = new Map(); // For toggling visibility
    this.sizeSwiper = null; // To hold the Swiper instance
    this.brandSwiper = null; // To hold the Brand Swiper instance
    this.typeSwiper = null; // To hold the Type Swiper instance
    this.ridingStyleSwiper = null; // To hold the Riding Style Swiper instance
    this.bootsSizeTypeSwiper = null; // To hold the Boots Size Type Swiper instance
    this.bootsSizeSwiper = null; // To hold the Boots Size Swiper instance
    this.readyForPopstate = false;

    // Advanced filter states
    this.riderLength = 170; // Default rider length in cm
    this.bootSize = 42; // Default boot size in EU
    this.deckWidth = 8.0; // Default skateboard deck width in inches
    this.riderLengthEnabled = false;
    this.bootSizeEnabled = false;
    this.deckWidthEnabled = false;

    // Boots size filter state
    this.bootsSizeType = 'euro'; // Default size type when boots filter exists
    this.bootsSizeFilter = []; // Selected boots sizes for the current size type
    this.hasBootsSizeFilter = !!this.filterWrapper.querySelector('.filter-group[data-filter-group="boots-size"]');
    if (!this.hasBootsSizeFilter) {
      this.bootsSizeType = null;
    }

    // Sorting state
    this.currentSort = { type: null, direction: null }; // null, 'asc', 'desc'

    this.typeTranslationMap = {
      Snowboard: { sv: 'Snowboard', en: 'Snowboard' },
      Bindning: { sv: 'Bindningar', en: 'Bindings' },
      Boots: { sv: 'Boots', en: 'Boots' },
      Skalkläder: { sv: 'Skalkläder', en: 'Outerwear' },
      Skalbyxor: { sv: 'Skalbyxor', en: 'Outerwear pants' },
      Skaljacka: { sv: 'Skaljackor', en: 'Outerwear jackets' },
      'T-Shirts': { sv: 'T-shirts', en: 'T-Shirts' },
      Underställ: { sv: 'Underställ', en: 'Base Layer' },
      Accessories: { sv: 'Tillbehör', en: 'Accessories' },
      Byxor: { sv: 'Byxor', en: 'Pants' },
      Handskar: { sv: 'Handskar', en: 'Gloves' },
      Mössor: { sv: 'Mössor', en: 'Beanies' },
      Keps: { sv: 'Keps', en: 'Hats' },
      Stighudar: { sv: 'Stighudar', en: 'Skins' },
      Valla: { sv: 'Valla', en: 'Wax' },
      Väskor: { sv: 'Väskor', en: 'Bags' },
      SplitboardTillbehör: { sv: 'SplitboardTillbehör', en: 'Splitboard accessories' },
      Plånbok: { sv: 'Plånbok', en: 'Wallet' },
      Skjorta: { sv: 'Skjortor', en: 'Shirts' },
      Caps: { sv: 'Keps', en: 'Hats' },
      Skärp: { sv: 'Skärp', en: 'Belts' },
      Jacka: { sv: 'Jackor', en: 'Jackets' },
      Tröjor: { sv: 'Tröjor', en: 'Sweaters' },
      Sunglasses: { sv: 'Solglasögon', en: 'Sunglasses' },
    };
    this.currentLocale = window.Shopify?.locale === 'en' ? 'en' : 'sv';
    // Canonicalize product types so filters keep working even when Shopify returns translated productType strings.
    // We store canonical keys in the URL/UI and only translate for display.
    this.typeCanonicalByLocale = { sv: new Map(), en: new Map() };
    Object.entries(this.typeTranslationMap).forEach(([canonical, tr]) => {
      const sv = tr?.sv || canonical;
      const en = tr?.en || canonical;
      this.typeCanonicalByLocale.sv.set(String(sv).trim(), canonical);
      this.typeCanonicalByLocale.en.set(String(en).trim(), canonical);
      // Also map canonical to itself to be safe.
      this.typeCanonicalByLocale.sv.set(String(canonical).trim(), canonical);
      this.typeCanonicalByLocale.en.set(String(canonical).trim(), canonical);
    });
    this.localePrefix = this.getLocalePrefix();

    // Grid icon URLs from data attributes (optional)
    // Use getAttribute to avoid dataset naming edge-cases with digits
    this.iconCols1 = this.filterWrapper?.getAttribute('data-icon-cols-1') || '';
    this.iconCols2 = this.filterWrapper?.getAttribute('data-icon-cols-2') || '';
    this.iconCols3 = this.filterWrapper?.getAttribute('data-icon-cols-3') || '';
    this.iconCols4 = this.filterWrapper?.getAttribute('data-icon-cols-4') || '';
    this.iconCols6 = this.filterWrapper?.getAttribute('data-icon-cols-6') || '';
    this.init();
  }

  canonicalizeProductType(typeValue) {
    if (!typeValue) return '';
    const raw = String(typeValue).trim();
    // Prefer the current locale, but fall back to the other locale to handle mixed/partial translations.
    const primary = this.currentLocale === 'en' ? 'en' : 'sv';
    const secondary = primary === 'en' ? 'sv' : 'en';
    return this.typeCanonicalByLocale?.[primary]?.get(raw) || this.typeCanonicalByLocale?.[secondary]?.get(raw) || raw;
  }

  getDisplayProductType(canonicalType) {
    if (!canonicalType) return '';
    return this.typeTranslationMap?.[canonicalType]?.[this.currentLocale] || canonicalType;
  }

  isSizeLikeOptionName(optionName) {
    if (!optionName) return false;
    const normalized = optionName.trim().toLowerCase();
    // Extendable list of option names that should be treated as "size" for filtering UI
    return normalized === 'size' || normalized === 'storlek' || normalized === 'length' || normalized === 'längd';
  }

  initializeSliderDropdowns() {
    // Initialize Rider Length dropdown
    const riderLengthSlider = document.getElementById('rider-length-slider');
    const riderLengthValue = document.getElementById('rider-length-value');

    if (riderLengthSlider && riderLengthValue) {
      const lengthContainer = document.createElement('div');
      lengthContainer.className = 'slider-value-container';

      const lengthSelect = document.createElement('select');
      lengthSelect.className = 'slider-value-select';
      lengthSelect.id = 'rider-length-select';

      // Range: 100-210cm
      for (let i = 100; i <= 210; i++) {
        const option = document.createElement('option');
        option.value = i;
        option.text = `${i} cm`;
        lengthSelect.appendChild(option);
      }
      lengthSelect.value = riderLengthSlider.value;

      // Sync slider with select
      riderLengthSlider.addEventListener('input', (e) => {
        lengthSelect.value = e.target.value;
      });

      // Sync select with slider
      lengthSelect.addEventListener('change', (e) => {
        riderLengthSlider.value = e.target.value;
        riderLengthSlider.dispatchEvent(new Event('input'));
        riderLengthSlider.dispatchEvent(new Event('change'));
      });

      lengthContainer.appendChild(lengthSelect);
      riderLengthValue.parentNode.replaceChild(lengthContainer, riderLengthValue);
    }

    // Initialize Boot Size dropdown
    const bootSizeSlider = document.getElementById('boot-size-slider');
    const bootSizeValue = document.getElementById('boot-size-value');

    if (bootSizeSlider && bootSizeValue) {
      const bootContainer = document.createElement('div');
      bootContainer.className = 'slider-value-container';

      const bootSelect = document.createElement('select');
      bootSelect.className = 'slider-value-select';
      bootSelect.id = 'boot-size-select';

      // Range: 33-50, step 0.5
      for (let i = 33; i <= 50; i += 0.5) {
        const option = document.createElement('option');
        option.value = i;
        option.text = `EU ${i}`;
        bootSelect.appendChild(option);
      }
      bootSelect.value = bootSizeSlider.value;

      // Sync slider with select
      bootSizeSlider.addEventListener('input', (e) => {
        bootSelect.value = e.target.value;
      });

      // Sync select with slider
      bootSelect.addEventListener('change', (e) => {
        bootSizeSlider.value = e.target.value;
        bootSizeSlider.dispatchEvent(new Event('input'));
        bootSizeSlider.dispatchEvent(new Event('change'));
      });

      bootContainer.appendChild(bootSelect);
      bootSizeValue.parentNode.replaceChild(bootContainer, bootSizeValue);
    }

    // Initialize Deck Width dropdown
    const deckWidthSlider = document.getElementById('deck-width-slider');
    const deckWidthValue = document.getElementById('deck-width-value');

    if (deckWidthSlider && deckWidthValue) {
      const deckContainer = document.createElement('div');
      deckContainer.className = 'slider-value-container';

      const deckSelect = document.createElement('select');
      deckSelect.className = 'slider-value-select';
      deckSelect.id = 'deck-width-select';

      const unit = this.currentLocale === 'en' ? 'in' : 'tum';
      for (let w = 7; w <= 10.5 + 0.001; w += 0.125) {
        const val = Math.round(w * 1000) / 1000;
        const option = document.createElement('option');
        option.value = val;
        option.text = `${parseFloat(val.toFixed(3))} ${unit}`;
        deckSelect.appendChild(option);
      }
      deckSelect.value = deckWidthSlider.value;

      deckWidthSlider.addEventListener('input', (e) => {
        deckSelect.value = e.target.value;
      });

      deckSelect.addEventListener('change', (e) => {
        deckWidthSlider.value = e.target.value;
        deckWidthSlider.dispatchEvent(new Event('input'));
        deckWidthSlider.dispatchEvent(new Event('change'));
      });

      deckContainer.appendChild(deckSelect);
      deckWidthValue.parentNode.replaceChild(deckContainer, deckWidthValue);
    }
  }

  getLocalePrefix() {
    const path = window.location.pathname;
    const match = path.match(/^\/([a-z]{2})(\/|$)/); // e.g., "/en/collections/..."
    return match ? `/${match[1]}` : ''; // returns "/en" or ""
  }

  getSortIcon(type, state) {
    // Icon URLs from data attributes (we'll add them)
    const iconMap = {
      name: {
        inactive: this.filterWrapper?.getAttribute('data-icon-sort-abc-1') || '/assets/icon-sort-abc-1.svg',
        asc: this.filterWrapper?.getAttribute('data-icon-sort-abc-2') || '/assets/icon-sort-abc-2.svg',
        desc: this.filterWrapper?.getAttribute('data-icon-sort-abc-3') || '/assets/icon-sort-abc-3.svg',
      },
      price: {
        inactive: this.filterWrapper?.getAttribute('data-icon-sort-price-1') || '/assets/icon-sort-price-1.svg',
        asc: this.filterWrapper?.getAttribute('data-icon-sort-price-2') || '/assets/icon-sort-price-2.svg',
        desc: this.filterWrapper?.getAttribute('data-icon-sort-price-3') || '/assets/icon-sort-price-3.svg',
      },
      created: {
        inactive: this.filterWrapper?.getAttribute('data-icon-sort-time-1') || '/assets/icon-sort-time-1.svg',
        asc: this.filterWrapper?.getAttribute('data-icon-sort-time-2') || '/assets/icon-sort-time-2.svg',
        desc: this.filterWrapper?.getAttribute('data-icon-sort-time-3') || '/assets/icon-sort-time-3.svg',
      },
    };

    const url = iconMap[type]?.[state];
    if (url) {
      const opacity = state === 'inactive' ? '0.4' : '1.0';
      return `<img src="${url}" alt="Sort ${type} ${state}" width="24" height="24" loading="lazy" style="opacity: ${opacity};" />`;
    }

    // Fallback SVG if icons not available
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style="opacity: ${
      state === 'inactive' ? '0.4' : '1.0'
    }"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
  }

  getColsIcon(columns) {
    // Prefer custom icon assets when provided
    const map = {
      1: this.iconCols1,
      2: this.iconCols2,
      3: this.iconCols3,
      4: this.iconCols4,
      6: this.iconCols6,
    };
    const url = map[columns];
    if (url) {
      return `<img src="${url}" alt="${columns} columns" width="24" height="24" loading="lazy" />`;
    }
    // Fallback: generate minimal bars inline
    const gap = 2;
    const left = 2;
    const right = 2;
    const top = 4;
    const height = 16;
    const innerWidth = 24 - left - right;
    const barWidth = Math.max(1, (innerWidth - (columns - 1) * gap) / columns);
    let bars = '';
    for (let i = 0; i < columns; i += 1) {
      const x = left + i * (barWidth + gap);
      bars += `<rect x="${x}" y="${top}" width="${barWidth}" height="${height}" rx="1" ry="1" />`;
    }
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">${bars}</svg>`;
  }

  async init() {
    this.initializeSliderDropdowns(); // Initialize dropdowns before anything else
    this.attachEventListeners();

    // Get filters from URL to apply on page load
    const filtersFromURL = this.getFiltersFromURL();
    filtersFromURL.bootsSizeFilterEnabled = this.hasBootsSizeFilter;
    if (this.hasBootsSizeFilter) {
      if (filtersFromURL.bootsSizeType) {
        this.bootsSizeType = filtersFromURL.bootsSizeType;
      } else {
        filtersFromURL.bootsSizeType = this.bootsSizeType || 'euro';
      }
      this.bootsSizeFilter = [...(filtersFromURL.bootsSizeFilter || [])];
    } else {
      this.bootsSizeType = null;
      this.bootsSizeFilter = [];
      filtersFromURL.bootsSizeType = null;
      filtersFromURL.bootsSizeFilter = [];
    }
    this.updatePaginationVisibility(filtersFromURL);

    this.showLoadingState();
    try {
      this.initialProducts = await this.fetchFilteredProducts({});
      this.renderFullGridOnce(); // Render all products once

      // Now, apply filters from URL and show/hide relevant cards
      this.forceApplyFilterUIState(filtersFromURL);
      const availableProducts = this.filterProductsInClient(this.initialProducts, filtersFromURL);
      this.updateGridVisibility(availableProducts);
      this.renderDynamicFilters(this.initialProducts, filtersFromURL);

      this.readyForPopstate = true; // Now safe for popstate logic
      this.injectGridControls();
      this.applyInitialGridPreference();
      this.loadSortPreference();

      // Set default sort to 'created' (relevance) if no preference exists
      if (!this.currentSort.type || !this.currentSort.direction) {
        this.currentSort.type = 'created';
        this.currentSort.direction = 'asc'; // Newest first
        this.saveSortPreference();
      }

      this.updateSortIcons();

      // Apply initial sorting
      if (this.currentSort.type && this.currentSort.direction) {
        this.applySorting();
      }
    } catch (error) {
      // Error during initial product fetch
    } finally {
      this.hideLoadingState();
    }
  }

  attachEventListeners() {
    // Handle boots size type selection (only when boots filter exists)
    if (this.hasBootsSizeFilter) {
      this.filterWrapper.addEventListener('click', (event) => {
        const sizeTypeElement = event.target.closest('[data-size-type]');
        if (sizeTypeElement) {
          const sizeType = sizeTypeElement.dataset.sizeType;
          if (sizeType) {
            // Update state
            this.bootsSizeType = sizeType;
            this.bootsSizeFilter = []; // Clear selected sizes when switching type

            // Update UI - remove selected class from all size types
            this.filterWrapper.querySelectorAll('[data-size-type]').forEach((el) => {
              el.classList.remove('selected', 'selected__sizerange');
            });
            // Add selected class to clicked element
            sizeTypeElement.classList.add(
              sizeTypeElement.classList.contains('swiper-slide') ? 'selected' : 'selected__sizerange'
            );

            // Trigger filter change
            this.handleFilterChange();
            return;
          }
        }
      });
    }

    // This single listener now handles clicks on slides for ANY filter group
    this.filterWrapper.addEventListener('click', (event) => {
      const slide = event.target.closest('.swiper-slide');
      if (slide && !slide.dataset.sizeType) {
        // Don't handle size type clicks here
        // Toggle the selected class
        slide.classList.toggle('selected');
        // Update boots size filter state if this is a boots size slide
        if (slide.closest('.filter-group[data-filter-group="boots-size"]')) {
          // Re-read the selected sizes from DOM to update state
          const selectedSizes = [];
          this.filterWrapper
            .querySelectorAll(
              '.filter-group[data-filter-group="boots-size"] #filter-boots-size-options .swiper-slide.selected'
            )
            .forEach((s) => {
              const norm = s.dataset.normalized;
              if (norm) {
                selectedSizes.push(norm);
              }
            });
          this.bootsSizeFilter = selectedSizes;
        }
        this.handleFilterChange();
      }
    });

    // Rider length slider and checkbox
    const riderLengthSlider = document.getElementById('rider-length-slider');
    const riderLengthCheckbox = document.getElementById('enable-rider-length');

    if (riderLengthCheckbox) {
      // Update filter group class on initial load
      const riderLengthFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="rider-length"]');
      if (riderLengthFilterGroup) {
        if (riderLengthCheckbox.checked) {
          riderLengthFilterGroup.classList.add('filter-active');
        } else {
          riderLengthFilterGroup.classList.remove('filter-active');
        }
      }

      riderLengthCheckbox.addEventListener('change', (e) => {
        this.riderLengthEnabled = e.target.checked;
        if (riderLengthSlider) {
          riderLengthSlider.disabled = !e.target.checked;
        }
        // Update filter group class based on checkbox state
        if (riderLengthFilterGroup) {
          if (e.target.checked) {
            riderLengthFilterGroup.classList.add('filter-active');
          } else {
            riderLengthFilterGroup.classList.remove('filter-active');
          }
        }
        this.handleFilterChange();
      });
    }

    if (riderLengthSlider) {
      riderLengthSlider.addEventListener('input', (e) => {
        this.riderLength = parseFloat(e.target.value);
      });

      riderLengthSlider.addEventListener('change', () => {
        if (this.riderLengthEnabled) {
          this.handleFilterChange();
        }
      });
    }

    // Boot size slider and checkbox
    const bootSizeSlider = document.getElementById('boot-size-slider');
    const bootSizeCheckbox = document.getElementById('enable-boot-size');

    if (bootSizeCheckbox) {
      // Update filter group class on initial load
      const bootSizeFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="boot-size"]');
      if (bootSizeFilterGroup) {
        if (bootSizeCheckbox.checked) {
          bootSizeFilterGroup.classList.add('filter-active');
        } else {
          bootSizeFilterGroup.classList.remove('filter-active');
        }
      }

      bootSizeCheckbox.addEventListener('change', (e) => {
        this.bootSizeEnabled = e.target.checked;
        if (bootSizeSlider) {
          bootSizeSlider.disabled = !e.target.checked;
        }
        // Update filter group class based on checkbox state
        if (bootSizeFilterGroup) {
          if (e.target.checked) {
            bootSizeFilterGroup.classList.add('filter-active');
          } else {
            bootSizeFilterGroup.classList.remove('filter-active');
          }
        }
        this.handleFilterChange();
      });
    }

    if (bootSizeSlider) {
      bootSizeSlider.addEventListener('input', (e) => {
        this.bootSize = parseFloat(e.target.value);
      });

      bootSizeSlider.addEventListener('change', () => {
        if (this.bootSizeEnabled) {
          this.handleFilterChange();
        }
      });
    }

    // Deck width slider and checkbox
    const deckWidthSlider = document.getElementById('deck-width-slider');
    const deckWidthCheckbox = document.getElementById('enable-deck-width');

    if (deckWidthCheckbox) {
      const deckWidthFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="deck-width"]');
      if (deckWidthFilterGroup) {
        if (deckWidthCheckbox.checked) {
          deckWidthFilterGroup.classList.add('filter-active');
        } else {
          deckWidthFilterGroup.classList.remove('filter-active');
        }
      }

      deckWidthCheckbox.addEventListener('change', (e) => {
        this.deckWidthEnabled = e.target.checked;
        if (deckWidthSlider) {
          deckWidthSlider.disabled = !e.target.checked;
        }
        if (deckWidthFilterGroup) {
          if (e.target.checked) {
            deckWidthFilterGroup.classList.add('filter-active');
          } else {
            deckWidthFilterGroup.classList.remove('filter-active');
          }
        }
        this.handleFilterChange();
      });
    }

    if (deckWidthSlider) {
      deckWidthSlider.addEventListener('input', (e) => {
        this.deckWidth = parseFloat(e.target.value);
      });

      deckWidthSlider.addEventListener('change', () => {
        if (this.deckWidthEnabled) {
          this.handleFilterChange();
        }
      });
    }

    // On back/forward navigation, force a page reload to ensure a clean state from the URL.
    // This is the simplest and most robust way to avoid popstate/DOM timing issues.
    window.addEventListener('popstate', () => {
      location.reload();
    });

    // Search input events
    const searchInput = this.filterWrapper.querySelector('#variant-filter-search');
    const clearBtn = this.filterWrapper.querySelector('.filter-options--search .field__clear');
    const hintEl = this.filterWrapper.querySelector('#variant-filter-search-hint');
    if (searchInput) {
      const applySearch = () => {
        const selected = this.getSelectedFilters();
        selected.q = searchInput.value.trim();
        this.updateFilterURL(selected);
        this.updatePaginationVisibility(selected);
        this.showLoadingState();
        try {
          const filtered = this.filterProductsInClient(this.initialProducts, selected);
          this.updateGridVisibility(filtered);
          this.renderDynamicFilters(this.initialProducts, selected);
          if (hintEl) {
            hintEl.textContent = selected.q ? `${filtered.length} träffar` : '';
          }

          // Apply current sorting after search
          if (this.currentSort.type && this.currentSort.direction) {
            this.applySorting();
          }
        } finally {
          this.hideLoadingState();
        }
      };

      let searchDebounce;
      searchInput.addEventListener('input', () => {
        clearBtn && (clearBtn.hidden = searchInput.value.length === 0);
        clearTimeout(searchDebounce);
        searchDebounce = setTimeout(applySearch, 200);
      });
      searchInput.addEventListener('change', applySearch);
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applySearch();
        }
      });

      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          searchInput.value = '';
          clearBtn.hidden = true;
          const selected = this.getSelectedFilters();
          selected.q = '';
          this.updateFilterURL(selected);
          this.updatePaginationVisibility(selected);
          const filtered = this.filterProductsInClient(this.initialProducts, selected);
          this.updateGridVisibility(filtered);
          this.renderDynamicFilters(this.initialProducts, selected);
          if (hintEl) hintEl.textContent = '';

          // Apply current sorting after clearing search
          if (this.currentSort.type && this.currentSort.direction) {
            this.applySorting();
          }
        });
      }
    }
  }

  /**
   * Gathers all selected filter values from the checkboxes.
   * @returns {object} - An object with keys for each filter group (e.g., 'size', 'brand')
   *                    and values as an array of selected options.
   *                    Example: { size: ['M', 'L'], brand: ['Patagonia'] }
   */
  getSelectedFilters() {
    const selected = {
      size: [],
      brand: [],
      type: [],
      ridingStyle: [],
      q: '',
      riderLength: this.riderLength,
      bootSize: this.bootSize,
      deckWidth: this.deckWidth,
      riderLengthEnabled: this.riderLengthEnabled,
      bootSizeEnabled: this.bootSizeEnabled,
      deckWidthEnabled: this.deckWidthEnabled,
      bootsSizeType: this.hasBootsSizeFilter ? this.bootsSizeType || 'euro' : null,
      bootsSizeFilter: this.hasBootsSizeFilter ? [...this.bootsSizeFilter] : [],
      bootsSizeFilterEnabled: this.hasBootsSizeFilter,
    };

    // Get selected brands from Swiper slides
    this.filterWrapper
      .querySelectorAll('.filter-group[data-filter-group="brand"] .swiper-slide.selected')
      .forEach((slide) => {
        if (slide.dataset.originalValue) {
          selected.brand.push(slide.dataset.originalValue);
        }
      });

    // Get selected sizes from Swiper slides
    this.filterWrapper
      .querySelectorAll('.filter-group[data-filter-group="size"] .swiper-slide.selected')
      .forEach((slide) => {
        const norm = slide.dataset.normalized;
        if (norm) {
          selected.size.push(norm);
        }
      });

    // Get selected types from Swiper slides
    this.filterWrapper
      .querySelectorAll('.filter-group[data-filter-group="type"] .swiper-slide.selected')
      .forEach((slide) => {
        if (slide.dataset.originalValue) {
          selected.type.push(slide.dataset.originalValue);
        }
      });

    // Get selected riding styles from Swiper slides
    this.filterWrapper
      .querySelectorAll('.filter-group[data-filter-group="riding-style"] .swiper-slide.selected')
      .forEach((slide) => {
        if (slide.dataset.originalValue) {
          selected.ridingStyle.push(slide.dataset.originalValue);
        }
      });

    // Get selected boots sizes from Swiper slides
    if (this.hasBootsSizeFilter) {
      selected.bootsSizeFilter = [];
      this.filterWrapper
        .querySelectorAll('.filter-group[data-filter-group="boots-size"] .swiper-slide.selected')
        .forEach((slide) => {
          const norm = slide.dataset.normalized;
          if (norm) {
            selected.bootsSizeFilter.push(norm);
          }
        });
      this.bootsSizeFilter = [...selected.bootsSizeFilter];
    }

    // Read current search value if present
    const searchInput = this.filterWrapper.querySelector('#variant-filter-search');
    if (searchInput) {
      selected.q = (searchInput.value || '').trim();
    }

    return selected;
  }

  /**
   * Sorts sizes according to a predefined order, with unknown sizes at the end.
   * This provides a logical, non-alphabetical sort order.
   * @param {string} a - First size
   * @param {string} b - Second size
   * @returns {number} - Sort order
   */
  customSizeSort(a, b) {
    const normalize = (val) => val?.toUpperCase()?.replace(/\s+/g, '') || '';

    const apparelOrder = ['XXS', 'XS', 'S', 'S-M', 'M', 'M-L', 'L', 'L-XL', 'XL', 'XXL', 'XXXL'];

    const pantsOrder = [
      '27',
      '28',
      'W28',
      'W28L30',
      'W28L32',
      '29',
      'W29',
      'W29L30',
      'W29L32',
      '30',
      'W30',
      'W30L30',
      'W30L32',
      '31',
      'W31',
      'W31L30',
      'W31L32',
      'W31L34',
      '32',
      'W32',
      'W32L30',
      'W32L32',
      'W32L34',
      '33',
      'W33',
      'W33L30',
      'W33L32',
      'W33L34',
      '34',
      'W34',
      'W34L30',
      'W34L32',
      'W34L34',
      '36',
      'W36',
      'W36L30',
      'W36L32',
      'W36L34',
    ];

    const menOrder = [
      'US5',
      'US55',
      'US6',
      'US65',
      'US7',
      'US75',
      'US8',
      'US85',
      'US9',
      'US95',
      'US10',
      'US105',
      'US11',
      'US115',
      'US12',
      'US125',
      'US13',
      'US14',
    ];

    const womenOrder = [
      'W-US5',
      'W-US55',
      'W-US6',
      'W-US65',
      'W-US7',
      'W-US75',
      'W-US8',
      'W-US85',
      'W-US9',
      'W-US95',
      'W-US10',
      'W-US105',
      'W-US11',
    ];

    const kidsOrder = [
      '27/10K',
      '28/11K',
      '29/12K',
      '30/13K',
      '31/1K',
      '32/2K',
      '33/3K',
      '34/3K',
      '35/4K',
      '36.5/5K',
      '38/6K',
      '40/7K',
    ];

    const normA = normalize(a);
    const normB = normalize(b);

    const getIndex = (val) => {
      const indexOr = (arr, prefix) => {
        const i = arr.indexOf(val);
        return i !== -1 ? `${prefix}-${i}` : null;
      };

      return (
        indexOr(apparelOrder, '1') ??
        indexOr(pantsOrder, '2') ??
        indexOr(menOrder, '3') ??
        indexOr(womenOrder, '4') ??
        indexOr(kidsOrder, '5') ??
        `9-${val}` // fallback group
      );
    };

    const aIndex = getIndex(normA);
    const bIndex = getIndex(normB);

    return aIndex.localeCompare(bIndex, undefined, { numeric: true, sensitivity: 'base' });
  }

  async handleFilterChange() {
    // Defensive check to prevent running if products haven't been loaded
    if (this.initialProducts.length === 0) {
      return;
    }

    const selectedFilters = this.getSelectedFilters();

    this.updateFilterURL(selectedFilters);
    this.updatePaginationVisibility(selectedFilters);

    this.showLoadingState();
    try {
      // Always filter from the complete initial list for consistency
      const filteredProducts = this.filterProductsInClient(this.initialProducts, selectedFilters);

      this.updateGridVisibility(filteredProducts);
      this.renderDynamicFilters(this.initialProducts, selectedFilters); // Update filters based on what's available in the full set

      // Apply current sorting after filtering
      if (this.currentSort.type && this.currentSort.direction) {
        this.applySorting();
      }
    } catch (error) {
      // Error fetching or rendering filtered products
    } finally {
      this.hideLoadingState();
      requestAnimationFrame(() => {
        const gridEl = document.getElementById('product-grid');
        if (gridEl) {
          // Get current scroll position
          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const gridTop = gridEl.offsetTop - 100;

          // Only scroll if the grid is below current position,
          // or if we're already above the grid (to scroll downto it)
          if (currentScrollTop < gridTop) {
            // We're above the grid, scroll down to it
            window.scrollTo({ top: gridTop, behavior: 'smooth' });
          }
          // If we're already below the grid, stay where we are (don't scroll)
        }
      });
    }
  }

  /**
   * Constructs and executes a GraphQL query to fetch filtered products.
   * @param {object} filters - The selected filters.
   * @returns {Promise<Array>} - A promise that resolves to an array of product objects.
   */
  async fetchFilteredProducts(filters) {
    const { query, variables } = this.buildGraphQLQuery(filters);

    const response = await fetch('/api/2024-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.shopifyConfig.storefrontAccessToken, // Public token
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`GraphQL request failed with status ${response.status}: ${errorBody}`);
    }

    const json = await response.json();
    if (json.errors) {
      throw new Error('GraphQL query returned errors.');
    }

    if (!json.data || !json.data.collection) {
      return [];
    }

    const nodes = json.data.collection.products.nodes;
    return window.inlandetHiddenProducts?.filterProducts(nodes) ?? nodes;
  }

  /**
   * Dynamically builds the GraphQL query string and variables.
   * @param {object} filters - The selected filter values.
   * @returns {object} - An object containing the GraphQL query string and variables.
   */
  buildGraphQLQuery(filters) {
    // This query is now simpler: it just fetches ALL relevant products once.
    // Filtering happens on the client side.
    const productFilters = [{ available: true }];

    const query = `
      query GetAllCollectionProducts($collectionId: ID!, $filters: [ProductFilter!]) {
        collection(id: $collectionId) {
          id
          title
          products(first: 250, filters: $filters) {
            nodes {
              id
              title
              handle
              tags
              vendor
              productType
              publishedAt
              onlineStoreUrl
              featuredImage {
                url(transform: { maxWidth: 1200 })
                altText
                width
                height
              }
              images(first: 2) {
                nodes {
                  url(transform: { maxWidth: 1200 })
                  altText
                  width
                  height
                }
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
                    metafield(namespace: "custom", key: "updated_date") {
                value
              }
              badges: metafield(namespace: "custom", key: "badges") {
                references(first: 10) {
                  nodes {
                    ... on Metaobject {
                      handle
                      fields {
                        key
                        value
                      }
                    }
                  }
                }
              }
              boardStyle: metafield(namespace: "custom", key: "board_style") {
                value
              }
              variants(first: 50) {
                nodes {
                  id
                  title
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  maxBootSize: metafield(namespace: "custom", key: "max_boot_size") {
                    value
                  }
                  riderLengthMin: metafield(namespace: "custom", key: "rider_length_min") {
                    value
                  }
                  riderLengthMax: metafield(namespace: "custom", key: "rider_length_max") {
                    value
                  }
                  minWidth: metafield(namespace: "custom", key: "min_width") {
                    value
                  }
                  maxWidth: metafield(namespace: "custom", key: "max_width") {
                    value
                  }
                  euroSize: metafield(namespace: "custom", key: "eurosize") {
                    value
                  }
                  mondoSize: metafield(namespace: "custom", key: "mondosize") {
                    value
                  }
                  usMens: metafield(namespace: "custom", key: "us_mens") {
                    value
                  }
                  usWomens: metafield(namespace: "custom", key: "us_womens") {
                    value
                  }
                  usKids: metafield(namespace: "custom", key: "us_kids") {
                    value
                  }
                }
              }
            }
          }
        }
      }
    `;

    return {
      query,
      variables: {
        collectionId: this.collectionId,
        filters: productFilters,
      },
    };
  }

  /**
   * Performs client-side filtering for sizes and any additional brands.
   * @param {Array} products - Products from the GraphQL response.
   * @param {object} filters - The selected filters.
   * @returns {Array} - The final filtered list of products.
   */
  filterProductsInClient(products, filters) {
    const {
      size,
      brand,
      type,
      ridingStyle,
      q,
      riderLength,
      bootSize,
      deckWidth,
      riderLengthEnabled,
      bootSizeEnabled,
      deckWidthEnabled,
      bootsSizeType,
      bootsSizeFilter,
      bootsSizeFilterEnabled = false,
    } = filters;

    return products.filter((product) => {
      // MASTER RULE: Product must have at least one available variant to be shown.
      const isAvailable = product.variants.nodes.some((v) => v.availableForSale);
      if (!isAvailable) {
        return false;
      }

      const brandMatch = !brand || brand.length === 0 || brand.includes(product.vendor);

      const sizeMatch =
        !size ||
        size.length === 0 ||
        product.variants.nodes.some(
          (variant) =>
            variant.availableForSale &&
            variant.selectedOptions.some(
              (option) => this.isSizeLikeOptionName(option.name) && size.includes(this.normalizeSize(option.value))
            )
        );

      const productTypeCanonical = this.canonicalizeProductType(product.productType);
      const typeMatch = !type || type.length === 0 || type.includes(productTypeCanonical);

      // Riding style match - check board_style metafield
      let ridingStyleMatch = true;
      if (ridingStyle && ridingStyle.length > 0) {
        if (product.boardStyle && product.boardStyle.value) {
          try {
            const boardStyles = JSON.parse(product.boardStyle.value);
            ridingStyleMatch = ridingStyle.some((style) => boardStyles.includes(style));
          } catch (e) {
            ridingStyleMatch = false;
          }
        } else {
          ridingStyleMatch = false;
        }
      }

      // Advanced filtering based on rider length and boot size
      let advancedMatch = true;
      if (riderLengthEnabled || bootSizeEnabled) {
        advancedMatch = product.variants.nodes.some((variant) => {
          if (!variant.availableForSale) return false;

          let lengthMatch = true;
          let bootMatch = true;

          if (riderLengthEnabled && variant.riderLengthMin && variant.riderLengthMax) {
            const minLength = parseFloat(variant.riderLengthMin.value) || 0;
            const maxLength = parseFloat(variant.riderLengthMax.value) || 0;
            lengthMatch = minLength <= riderLength && maxLength >= riderLength;
          } else if (riderLengthEnabled) {
            // If filter is enabled but metafield missing, exclude this variant
            lengthMatch = false;
          }

          if (bootSizeEnabled && variant.maxBootSize) {
            const maxBoot = parseFloat(variant.maxBootSize.value) || 0;
            bootMatch = maxBoot >= bootSize;
          } else if (bootSizeEnabled) {
            // If filter is enabled but metafield missing, exclude this variant
            bootMatch = false;
          }

          return lengthMatch && bootMatch;
        });
      }

      // Deck width filtering for skateboard trucks
      let deckWidthMatch = true;
      if (deckWidthEnabled) {
        deckWidthMatch = product.variants.nodes.some((variant) => {
          if (!variant.availableForSale) return false;

          if (variant.minWidth && variant.maxWidth) {
            const minW = parseFloat(variant.minWidth.value) || 0;
            const maxW = parseFloat(variant.maxWidth.value) || 0;
            return minW <= deckWidth && maxW >= deckWidth;
          }

          return false;
        });
      }

      // Boots size filtering based on custom meta fields
      let bootsSizeMatch = true;
      if (bootsSizeFilterEnabled && bootsSizeType) {
        // Map size type to the corresponding meta field key
        const sizeTypeMap = {
          euro: 'euroSize',
          mondo: 'mondoSize',
          us_mens: 'usMens',
          us_womens: 'usWomens',
          us_kids: 'usKids',
        };
        const metaFieldKey = sizeTypeMap[bootsSizeType];

        if (metaFieldKey) {
          // First, filter by size type - product must have at least one variant with this size type
          const hasSizeType = product.variants.nodes.some((variant) => {
            if (!variant.availableForSale) return false;
            const metaField = variant[metaFieldKey];
            return metaField && metaField.value;
          });

          if (!hasSizeType) {
            bootsSizeMatch = false;
          } else if (bootsSizeFilter && bootsSizeFilter.length > 0) {
            // Further filter by selected sizes within the size type
            bootsSizeMatch = product.variants.nodes.some((variant) => {
              if (!variant.availableForSale) return false;
              const metaField = variant[metaFieldKey];
              if (!metaField || !metaField.value) return false;

              // Normalize the meta field value for comparison based on size type
              const normalizedValue = this.normalizeBootsSize(metaField.value, bootsSizeType);
              return bootsSizeFilter.includes(normalizedValue);
            });
          }
        }
      }

      const query = (q || '').trim().toLowerCase();
      const searchMatch =
        !query ||
        (product.title || '').toLowerCase().includes(query) ||
        (product.vendor || '').toLowerCase().includes(query) ||
        (product.productType || '').toLowerCase().includes(query) ||
        (productTypeCanonical || '').toLowerCase().includes(query);

      return (
        brandMatch &&
        sizeMatch &&
        typeMatch &&
        ridingStyleMatch &&
        advancedMatch &&
        deckWidthMatch &&
        bootsSizeMatch &&
        searchMatch
      );
    });
  }

  updateGridVisibility(products) {
    const visibleHandles = new Set(products.map((p) => p.handle));

    this.productCardMap.forEach((cardElement, handle) => {
      if (visibleHandles.has(handle)) {
        cardElement.style.display = '';
      } else {
        cardElement.style.display = 'none';
      }
    });

    // Apply sorting to visible products if sorting is active
    if (this.currentSort.type && this.currentSort.direction) {
      this.reorderVisibleCards(products);
    }
  }

  reorderVisibleCards(products) {
    if (!this.productGrid || !this.currentSort.type || !this.currentSort.direction) return;

    // Sort the products first
    const sortedProducts = this.sortProducts(products, this.currentSort.type, this.currentSort.direction);

    // Get all current cards in DOM
    const allCards = Array.from(this.productGrid.children);

    // Create map of handle to card element
    const cardMap = new Map();
    allCards.forEach((card) => {
      const handle = card.querySelector('.card-wrapper')?.dataset?.productHandle;
      if (handle) {
        cardMap.set(handle, card);
      }
    });

    // Create fragment with sorted visible cards first
    const fragment = document.createDocumentFragment();

    // Add sorted visible cards
    sortedProducts.forEach((product) => {
      const card = cardMap.get(product.handle);
      if (card) {
        fragment.appendChild(card);
      }
    });

    // Add remaining hidden cards at the end
    const sortedHandles = new Set(sortedProducts.map((p) => p.handle));
    allCards.forEach((card) => {
      const handle = card.querySelector('.card-wrapper')?.dataset?.productHandle;
      if (handle && !sortedHandles.has(handle)) {
        fragment.appendChild(card);
      }
    });

    // Replace grid content
    this.productGrid.innerHTML = '';
    this.productGrid.appendChild(fragment);
  }

  updatePaginationVisibility(filters) {
    const filtersActive =
      filters.size?.length > 0 ||
      filters.brand?.length > 0 ||
      filters.type?.length > 0 ||
      (filters.q && filters.q.length > 0);

    const showPagination = () => {
      const wrapper = document.querySelector('.pagination-wrapper');
      const nav = document.querySelector('.pagination');
      if (wrapper) wrapper.style.removeProperty('display');
      if (nav) nav.style.removeProperty('display');
    };

    if (!filtersActive) {
      showPagination();
      return;
    }

    // If filters ARE active, hide the pagination. This is aggressive and polls
    // for the elements, targeting both the wrapper and the nav itself.
    let attempts = 0;
    const maxAttempts = 20; // Try for up to 2 seconds
    const interval = 100;

    const findAndHide = () => {
      const wrapper = document.querySelector('.pagination-wrapper');
      const nav = document.querySelector('.pagination');
      let hidden = false;

      if (wrapper) {
        wrapper.style.setProperty('display', 'none', 'important');
        hidden = true;
      }

      // Also hide the nav itself in case the wrapper isn't used consistently.
      if (nav) {
        nav.style.setProperty('display', 'none', 'important');
        hidden = true;
      }

      if (!hidden && attempts < maxAttempts) {
        attempts++;
        setTimeout(findAndHide, interval);
      }
    };

    findAndHide();
  }

  _initializeCurrencyFormatter() {
    this.userLocale = navigator.language || 'sv-SE';
    const shopifyCurrency = window?.Shopify?.currency;
    const firstProductCurrency = this.initialProducts[0]?.priceRange.minVariantPrice.currencyCode || 'SEK';
    this.currentCurrency = shopifyCurrency?.active || firstProductCurrency || 'SEK';
    this.conversionRate = shopifyCurrency && shopifyCurrency.rate ? parseFloat(shopifyCurrency.rate) : 1;
  }

  formatCurrency(val) {
    if (typeof val !== 'number') return '';
    const convertedValue = val * this.conversionRate;
    const roundedValue = Math.round(convertedValue);

    if (this.currentCurrency === 'SEK') {
      const formattedVal = new Intl.NumberFormat('sv-SE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(roundedValue);
      return `${formattedVal} SEK`;
    }

    if (this.currentCurrency === 'EUR') {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(roundedValue);
    }

    try {
      return new Intl.NumberFormat(this.userLocale, {
        style: 'currency',
        currency: this.currentCurrency,
      }).format(convertedValue);
    } catch (e) {
      return `${convertedValue.toFixed(2)} ${this.currentCurrency}`;
    }
  }

  populateCardImage(cardWrapper, product) {
    const mediaWrapper = cardWrapper.querySelector('.card__media .media');
    if (mediaWrapper && product.featuredImage?.url) {
      mediaWrapper.innerHTML = '';

      const primary = product.featuredImage;
      const images = Array.isArray(product.images?.nodes) ? product.images.nodes : [];
      const secondary = images.find((img) => img?.url && img.url !== primary.url) || images[1] || images[0];

      // Always append primary image
      const makeImg = (img) => {
        const el = document.createElement('img');
        const src = img.url;
        el.src = `${src}&width=533`;
        el.srcset = `${src}&width=165 165w, ${src}&width=360 360w, ${src}&width=533 533w, ${src}&width=720 720w`;
        el.sizes =
          '(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';
        el.alt = img.altText || product.title;
        el.width = img.width || 500;
        el.height = img.height || 500;
        el.loading = 'lazy';
        el.className = 'motion-reduce';
        return el;
      };

      const firstImg = makeImg(primary);
      mediaWrapper.appendChild(firstImg);

      if (secondary && secondary.url && secondary.url !== primary.url) {
        // Enable hover effect by having two stacked images
        const secondImg = makeImg(secondary);
        mediaWrapper.classList.add('media--hover-effect');
        mediaWrapper.appendChild(secondImg);

        // Mobile swipe: toggle secondary image and suppress the immediate click navigation
        const enableSwipeToggle = () => {
          let startX = null;
          let startY = null;
          let inMedia = false;
          let isSwiping = false;
          let suppressClick = false;

          const isPointInsideMedia = (x, y) => {
            const rect = mediaWrapper.getBoundingClientRect();
            return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
          };

          // Attach to the card wrapper to catch events even if a link overlay sits above the media
          const host = cardWrapper;

          host.addEventListener(
            'touchstart',
            (e) => {
              const touch = e.changedTouches?.[0];
              if (!touch) return;
              startX = touch.clientX;
              startY = touch.clientY;
              inMedia = isPointInsideMedia(startX, startY);
              isSwiping = false;
            },
            { passive: true }
          );

          host.addEventListener(
            'touchmove',
            (e) => {
              if (!inMedia || startX == null || startY == null) return;
              const touch = e.changedTouches?.[0];
              if (!touch) return;
              const dx = touch.clientX - startX;
              const dy = touch.clientY - startY;
              if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
                isSwiping = true;
                // prevent vertical scroll hijack when we are swiping horizontally
                e.preventDefault();
              }
            },
            { passive: false }
          );

          host.addEventListener(
            'touchend',
            (e) => {
              if (!inMedia || startX == null) return;
              const touch = e.changedTouches?.[0];
              const endX = touch?.clientX ?? startX;
              const dx = endX - startX;
              if (isSwiping && Math.abs(dx) > 20) {
                mediaWrapper.classList.toggle('is-swapped');
                suppressClick = true;
                setTimeout(() => {
                  suppressClick = false;
                }, 250);
              }
              startX = null;
              startY = null;
              inMedia = false;
              isSwiping = false;
            },
            { passive: true }
          );

          // Suppress click immediately following a swipe to avoid navigating
          host.addEventListener(
            'click',
            (e) => {
              if (suppressClick && inMedia) {
                e.preventDefault();
                e.stopPropagation();
              }
            },
            true
          );
        };

        if (window.innerWidth < 990) enableSwipeToggle();
      }
    } else if (mediaWrapper) {
      mediaWrapper.innerHTML =
        '<div class="media-placeholder" style="width: 100%; height: 100%; display: grid; place-content: center; background-color: #f2f2f2;">Bild saknas</div>';
    }
  }

  populateCardHeader(cardWrapper, product) {
    const productIdNum = product.id.split('/').pop();
    const productUrl = `${this.localePrefix}/products/${product.handle}`;
    const infoContainer = cardWrapper.querySelector('.card__information');
    const headingEl = infoContainer?.querySelector('.card__heading');

    if (infoContainer && headingEl && product.vendor) {
      const oldVendorEl = infoContainer.querySelector('.vendor-display');
      if (oldVendorEl) oldVendorEl.remove();
      const vendorSpan = document.createElement('span');
      vendorSpan.className = 'visually-hidden';
      vendorSpan.textContent = 'Säljare:';
      infoContainer.insertBefore(vendorSpan, headingEl);
      const newVendorEl = document.createElement('div');
      newVendorEl.className = 'caption-with-letter-spacing light';
      newVendorEl.textContent = product.vendor;
      infoContainer.insertBefore(newVendorEl, headingEl);
    }

    cardWrapper.querySelectorAll('a').forEach((a) => {
      if (!a.href.includes('?variant=')) a.href = productUrl;
    });
    if (headingEl) {
      headingEl.classList.remove('card__heading--placeholder');
      headingEl.classList.add('h5');
      headingEl.id = `title-template--filtered-${productIdNum}`;
      const link = headingEl.querySelector('a');
      if (link) {
        link.textContent = product.title;
        link.removeAttribute('aria-disabled');
        link.id = `CardLink-template--filtered-${productIdNum}`;
        link.setAttribute('aria-labelledby', `${link.id} Badge-template--filtered-${productIdNum}`);
      } else {
        headingEl.textContent = product.title;
      }
    }
  }

  populateCardPrice(cardWrapper, product) {
    const priceContainer = cardWrapper.querySelector('.price');
    if (!priceContainer) return;

    const price = Number(product.priceRange?.minVariantPrice?.amount);
    const compareAt = Number(product.compareAtPriceRange?.maxVariantPrice?.amount);
    const isOnSale = !isNaN(compareAt) && compareAt > 0 && compareAt > price;
    const isSoldOut = !product.variants.nodes.some((v) => v.availableForSale);

    priceContainer.classList.remove('price--on-sale', 'price--sold-out');

    let priceHTML = '';

    if (isSoldOut) {
      priceContainer.classList.add('price--sold-out');
      priceHTML = `
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item">Slut i lager</span>
          </div>
        </div>`;
    } else if (!isNaN(price)) {
      const priceFormatted = this.formatCurrency(price);
      const compareAtFormatted = this.formatCurrency(compareAt);

      if (isOnSale) {
        priceContainer.classList.add('price--on-sale');
        priceHTML = `
          <div class="price__container">
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Ordinarie pris</span>
              <span>
                <s class="price-item price-item--regular">${compareAtFormatted}
              </span>
              <span class="visually-hidden visually-hidden--inline">Försäljningspris</span>
              <span class="price-item price-item--sale price-item--last">${priceFormatted}</span>
            </div>
          </div>`;
      } else {
        priceHTML = `
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Ordinarie pris</span>
              <span class="price-item price-item--regular">${priceFormatted}</span>
            </div>
          </div>`;
      }
    }

    priceContainer.innerHTML = priceHTML;
  }

  populateCardSizes(cardWrapper, product) {
    const productIdNum = product.id.split('/').pop();
    const cardContent = cardWrapper.querySelector('.card__content');
    if (!cardContent) return;

    // --- Cleanup: Remove any existing buttons/links before adding new ones ---
    const oldQuickAdd = cardContent.querySelector('.quick-add');
    if (oldQuickAdd) oldQuickAdd.remove();

    const availableVariants = product.variants.nodes.filter((v) => v.availableForSale);

    // If there are no available variants, there's nothing more to do.
    if (availableVariants.length === 0) {
      return;
    }

    // Determine if the product has a "Size" or "Length" option across ANY of its variants, even unavailable ones.
    const productHasSizeOption = product.variants.nodes.some((variant) =>
      variant.selectedOptions.some((option) => this.isSizeLikeOptionName(option.name))
    );

    // A "simple product" for our purposes is one with a single variant that does not have a size option.
    if (availableVariants.length === 1 && !productHasSizeOption) {
      // --- SIMPLE PRODUCT: Use native Add to Cart template ---
      const template = document.getElementById('product-card-add-to-cart-template');
      if (template) {
        const addToCart = template.content.cloneNode(true).querySelector('.quick-add');
        const form = addToCart.querySelector('form');
        const inputId = addToCart.querySelector('input[name="id"]');

        inputId.value = availableVariants[0].id.split('/').pop();
        form.id = `quick-add-filtered-${productIdNum}`;
        form.querySelector('button').id = `quick-add-filtered-${productIdNum}-submit`;

        cardContent.appendChild(addToCart);
      }
    } else {
      // --- SIZED PRODUCT or MULTI-VARIANT PRODUCT: Render "Choose options" button ---
      const sizeLinks = availableVariants
        .map((v) => {
          const sizeOpt = v.selectedOptions.find((opt) => this.isSizeLikeOptionName(opt.name));
          const variantIdNum = v.id.split('/').pop();
          return sizeOpt
            ? `<a href="${this.localePrefix}/products/${product.handle}?variant=${variantIdNum}">${sizeOpt.value}</a>`
            : '';
        })
        .filter(Boolean)
        .join(' &middot; ');

      const quickAddContainer = document.createElement('div');
      quickAddContainer.className = 'quick-add no-js-hidden';

      let htmlContent = '';
      if (sizeLinks) {
        htmlContent += `<div class="caption-with-letter-spacing light">${sizeLinks}</div>`;
      }

      htmlContent += `
        <modal-opener data-modal="#QuickAdd-${productIdNum}">
          <button id="quick-add-template--filtered-${productIdNum}-submit" type="submit" name="add"
            class="quick-add__submit button button--full-width button--secondary"
            aria-haspopup="dialog"
            aria-labelledby="quick-add-template--filtered-${productIdNum}-submit title-template--filtered-${productIdNum}"
            data-product-url="${this.localePrefix}/products/${product.handle}">
            Välj alternativ
            <div class="loading__spinner hidden">
              <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
            </div>
          </button>
        </modal-opener>
      `;

      quickAddContainer.innerHTML = htmlContent;
      cardContent.appendChild(quickAddContainer);
    }
  }

  populateCardBadge(cardWrapper, product) {
    const productIdNum = product.id.split('/').pop();
    // The badge must be inside .card__inner to overlay the image.
    const cardInner = cardWrapper.querySelector('.card__inner');
    if (!cardInner) return;

    // Clean up any badge from the template to prevent duplicates.
    const existingBadge = cardInner.querySelector('.card__badge');
    if (existingBadge) existingBadge.remove();
    // Also check the content area, as that's where we previously put it.
    const contentBadge = cardWrapper.querySelector('.card__content .card__badge');
    if (contentBadge) contentBadge.remove();

    const price = Number(product.priceRange?.minVariantPrice?.amount);
    const compareAt = Number(product.compareAtPriceRange?.maxVariantPrice?.amount);
    const isOnSale = !isNaN(compareAt) && compareAt > 0 && compareAt > price;

    if (isOnSale) {
      // Create the badge element fresh and append it to the correct parent.
      const badgeEl = document.createElement('div');

      const discountPercent = Math.round(((compareAt - price) / compareAt) * 100);
      const discountAmount = compareAt - price;

      let badgeText;
      if (discountPercent >= 20) {
        badgeText = `-${discountPercent}%`;
      } else {
        badgeText = `-${this.formatCurrency(discountAmount)}`;
      }

      badgeEl.className = 'card__badge bottom left';
      badgeEl.innerHTML = `
        <span id="Badge-template--filtered-${productIdNum}" class="badge badge--bottom-left color-scheme-4">
          ${badgeText}
        </span>
      `;

      cardInner.appendChild(badgeEl);
    }
  }

  populateCustomBadges(cardWrapper, product) {
    // Populate custom badges from metafields (from GraphQL)
    const cardInformation = cardWrapper.querySelector('.card__information');
    if (!cardInformation) return;

    // Remove any existing custom badges to prevent duplicates
    const existingBadges = cardInformation.querySelector('.product-badges');
    if (existingBadges) existingBadges.remove();

    // Check if product has badges metafield data from GraphQL
    const badgesData = product.badges?.references?.nodes;
    if (!badgesData || badgesData.length === 0) return;

    // Determine current language
    const locale = document.documentElement.lang || 'sv';
    const isSwedish = locale.toLowerCase().includes('sv');

    // Create badges container
    const badgesContainer = document.createElement('div');
    badgesContainer.className = 'product-badges';
    badgesContainer.setAttribute('aria-label', isSwedish ? 'Produktetiketter' : 'Product badges');

    const badgesList = document.createElement('div');
    badgesList.className = 'product-badges__list';
    badgesList.setAttribute('data-context', 'collection');
    badgesList.setAttribute('data-limit', '2'); // Default, JS will update

    // Render each badge
    badgesData.forEach((badgeNode) => {
      const fields = {};
      badgeNode.fields?.forEach((field) => {
        fields[field.key] = field.value;
      });

      const label = isSwedish
        ? fields.label_sv || fields.label_en || badgeNode.handle
        : fields.label_en || fields.label_sv || badgeNode.handle;

      const bgColor = fields.bg_color || '#6B7280';
      const textColor = fields.text_color || '#FFFFFF';

      const badgeSpan = document.createElement('span');
      badgeSpan.className = 'product-badges__badge';
      badgeSpan.style.setProperty('--badge-bg', bgColor);
      badgeSpan.style.setProperty('--badge-text', textColor);
      badgeSpan.setAttribute('aria-label', label);
      badgeSpan.textContent = label;

      badgesList.appendChild(badgeSpan);
    });

    badgesContainer.appendChild(badgesList);

    // Insert at the beginning of card__information (before vendor/title)
    cardInformation.insertBefore(badgesContainer, cardInformation.firstChild);
  }

  createProductCard(product) {
    const template = document.getElementById('product-card-template');
    if (!template) {
      return null;
    }

    const clone = template.content.cloneNode(true);
    const cardElement = clone.firstElementChild;
    if (!cardElement) return null;

    const cardWrapper = cardElement.querySelector('.card-wrapper');
    if (!cardWrapper) return null;

    // Ensure the card has the 'card--media' class for consistent styling with server-rendered cards.
    const cardDiv = cardWrapper.querySelector('.card');
    if (cardDiv) {
      cardDiv.classList.add('card--media');
    }

    cardWrapper.dataset.productHandle = product.handle;

    this.populateCardImage(cardWrapper, product);
    this.populateCardHeader(cardWrapper, product);
    this.populateCardPrice(cardWrapper, product);
    this.populateCardSizes(cardWrapper, product);
    this.populateCardBadge(cardWrapper, product);
    this.populateCustomBadges(cardWrapper, product);

    return cardElement;
  }

  renderFullGridOnce() {
    if (!this.productGrid) {
      return;
    }

    this.productGrid.innerHTML = '';
    this.productCardMap.clear();

    this._initializeCurrencyFormatter();

    this.initialProducts.forEach((product) => {
      const card = this.createProductCard(product);
      if (card) {
        this.productGrid.appendChild(card);
        this.productCardMap.set(product.handle, card);
      }
    });

    // Update badge limits based on actual grid columns
    this.updateBadgeLimits();

    // Update limits on resize
    const ro = new ResizeObserver(() => this.updateBadgeLimits());
    if (this.productGrid) ro.observe(this.productGrid);

    document.dispatchEvent(new Event('shopify:section:load'));
  }

  updateBadgeLimits() {
    // Mirror the logic from product-badges.js
    const MAP = (cols) => {
      if (cols === 1) return 2; // Mobile: 2 badges
      if (cols === 2) return 2;
      if (cols === 3) return 3;
      if (cols === 4) return 3;
      if (cols >= 6) return 2;
      return 2;
    };

    const getCols = (gridEl) => {
      const style = window.getComputedStyle(gridEl);
      const template = style.getPropertyValue('grid-template-columns');
      if (!template) return null;
      return template.split(' ').length || null;
    };

    if (!this.productGrid) return;

    const cols = getCols(this.productGrid) || 2;
    const limit = MAP(cols);
    const lists = this.productGrid.querySelectorAll('.product-badges__list[data-context="collection"]');

    lists.forEach((list) => {
      list.setAttribute('data-limit', String(limit));
    });
  }

  injectGridControls() {
    // Only for pages with variant filter
    const container = this.productGrid
      ? this.productGrid.parentElement
      : document.getElementById('ProductGridContainer');
    if (!container || container.querySelector('.variant-grid-controls')) return;

    const controls = document.createElement('div');
    controls.className = 'variant-grid-controls';
    controls.innerHTML = `
      <div class="sort-controls">
        <button type="button" data-sort="created" aria-label="Sortera efter tid" title="Sortera efter tid (Ny → Gammal)"><span class="icon">${this.getSortIcon(
          'created',
          'inactive'
        )}</span></button>
        <button type="button" data-sort="name" aria-label="Sortera efter produktnamn" title="Sortera efter namn (A → Z)"><span class="icon">${this.getSortIcon(
          'name',
          'inactive'
        )}</span></button>
        <button type="button" data-sort="price" aria-label="Sortera efter pris" title="Sortera efter pris (Billig → Dyr)"><span class="icon">${this.getSortIcon(
          'price',
          'inactive'
        )}</span></button>
      </div>
      <div class="grid-controls">
        <div class="desktop-controls">
          <button type="button" data-cols="3" aria-label="Show 3 columns"><span class="icon">${this.getColsIcon(
            3
          )}</span></button>
          <button type="button" data-cols="4" aria-label="Show 4 columns"><span class="icon">${this.getColsIcon(
            4
          )}</span></button>
          <button type="button" data-cols="6" aria-label="Show 6 columns"><span class="icon">${this.getColsIcon(
            6
          )}</span></button>
        </div>
        <div class="mobile-controls">
          <button type="button" data-cols="1" aria-label="Show 1 column"><span class="icon">${this.getColsIcon(
            1
          )}</span></button>
          <button type="button" data-cols="2" aria-label="Show 2 columns"><span class="icon">${this.getColsIcon(
            2
          )}</span></button>
        </div>
      </div>
    `;

    container.insertBefore(controls, container.firstChild);

    const updateActive = () => {
      const isDesktop = window.innerWidth >= 990;
      const pref = this.getGridPreference();
      controls.querySelectorAll('button').forEach((btn) => btn.classList.remove('active'));
      if (isDesktop) {
        const btn = controls.querySelector(`.desktop-controls button[data-cols="${pref.desktop}"]`);
        if (btn) btn.classList.add('active');
      } else {
        const btn = controls.querySelector(`.mobile-controls button[data-cols="${pref.mobile}"]`);
        if (btn) btn.classList.add('active');
      }
    };

    controls.addEventListener('click', (e) => {
      const sortBtn = e.target.closest('button[data-sort]');
      const colsBtn = e.target.closest('button[data-cols]');

      if (sortBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleSortClick(sortBtn.getAttribute('data-sort'));
        return;
      }

      if (colsBtn) {
        e.preventDefault();
        e.stopPropagation();
        const value = colsBtn.getAttribute('data-cols');
        const isDesktop = window.innerWidth >= 990;
        const pref = this.getGridPreference();
        if (isDesktop) {
          pref.desktop = value;
        } else {
          pref.mobile = value;
        }
        this.setGridPreference(pref);
        this.applyGridPreference(pref);
        updateActive();
      }
    });

    window.addEventListener('resize', () => {
      updateActive();
      this.applyGridPreference(this.getGridPreference());
    });
    updateActive();
  }

  getGridPreference() {
    try {
      const raw = localStorage.getItem('variantGridPreference');
      const parsed = raw ? JSON.parse(raw) : null;
      return {
        desktop: parsed?.desktop || '4',
        mobile: parsed?.mobile || '2',
      };
    } catch {
      return { desktop: '4', mobile: '2' };
    }
  }

  setGridPreference(pref) {
    try {
      localStorage.setItem('variantGridPreference', JSON.stringify(pref));
    } catch {}
  }

  applyGridPreference(pref) {
    if (!this.productGrid) return;
    // Remove our custom helper classes (if any remain)
    this.productGrid.classList.remove(
      'variant-cols-desktop-4',
      'variant-cols-desktop-6',
      'variant-cols-mobile-1',
      'variant-cols-mobile-2'
    );

    // Dawn grid is flex-based; control is via grid--X-col-desktop and grid--X-col-tablet(-down)
    // Collect then remove matching classes to avoid mutating while iterating
    const classesToRemove = [];
    this.productGrid.classList.forEach((cls) => {
      if (
        /^grid--\d+-col-desktop$/.test(cls) ||
        /^grid--\d+-col-tablet$/.test(cls) ||
        /^grid--\d+-col-tablet-down$/.test(cls)
      ) {
        classesToRemove.push(cls);
      }
    });
    classesToRemove.forEach((cls) => this.productGrid.classList.remove(cls));

    // Add desired classes based on preference
    const desktopClass = `grid--${pref.desktop}-col-desktop`;
    const mobileClass = `grid--${pref.mobile}-col-tablet-down`;
    this.productGrid.classList.add(desktopClass, mobileClass);
  }

  applyInitialGridPreference() {
    const pref = this.getGridPreference();
    this.applyGridPreference(pref);
  }

  handleSortClick(sortType) {
    if (this.currentSort.type === sortType) {
      // Same sort type clicked, toggle between asc and desc
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      // Different sort type clicked, deactivate old and activate new
      this.currentSort.type = sortType;
      this.currentSort.direction = 'asc'; // Start with ascending (Billig, A-Z, Ny)
    }

    this.updateSortIcons();
    this.applySorting();
    this.saveSortPreference();
  }

  updateSortIcons() {
    const controls = document.querySelector('.variant-grid-controls');
    if (!controls) return;

    const sortButtons = controls.querySelectorAll('.sort-controls button[data-sort]');
    sortButtons.forEach((btn) => {
      const sortType = btn.getAttribute('data-sort');
      let state = 'inactive';

      if (this.currentSort.type === sortType && this.currentSort.direction) {
        state = this.currentSort.direction; // 'asc' or 'desc'
      }

      const iconSpan = btn.querySelector('.icon');
      if (iconSpan) {
        iconSpan.innerHTML = this.getSortIcon(sortType, state);
      }

      // Update tooltip based on current state
      btn.title = this.getSortTooltip(sortType, state);
    });
  }

  getSortTooltip(sortType, state) {
    const tooltips = {
      created: {
        inactive: 'Sortera efter relevans (Senast uppdaterad)',
        asc: 'Sorterat efter relevans: Senast uppdaterad först (klicka för äldst först)',
        desc: 'Sorterat efter relevans: Äldst först (klicka för senast uppdaterad först)',
      },
      name: {
        inactive: 'Sortera efter namn (A → Z)',
        asc: 'Sorterat efter namn: A → Z (klicka för Z → A)',
        desc: 'Sorterat efter namn: Z → A (klicka för A → Z)',
      },
      price: {
        inactive: 'Sortera efter pris (Billig → Dyr)',
        asc: 'Sorterat efter pris: Billig → Dyr (klicka för Dyr → Billig)',
        desc: 'Sorterat efter pris: Dyr → Billig (klicka för Billig → Dyr)',
      },
    };

    return tooltips[sortType]?.[state] || `Sortera efter ${sortType}`;
  }

  applySorting() {
    if (!this.currentSort.type || !this.currentSort.direction) {
      // No sorting, maintain current grid order
      return;
    }

    // Get current visible products
    const selectedFilters = this.getSelectedFilters();
    const filteredProducts = this.filterProductsInClient(this.initialProducts, selectedFilters);

    // Use the new reorder method that works with current visibility
    this.reorderVisibleCards(filteredProducts);
  }

  sortProducts(products, sortType, direction) {
    const sorted = [...products].sort((a, b) => {
      let comparison = 0;

      switch (sortType) {
        case 'name':
          comparison = a.title.localeCompare(b.title, 'sv', { sensitivity: 'base' });
          break;
        case 'price':
          const priceA = Number(a.priceRange?.minVariantPrice?.amount) || 0;
          const priceB = Number(b.priceRange?.minVariantPrice?.amount) || 0;
          comparison = priceA - priceB;
          break;
        case 'created':
          // Använd ditt anpassade metafälts-värde (custom.updated_date), ELLER falla tillbaka på publiceringsdatumet.
          const dateA_str = a.metafield?.value || a.publishedAt;
          const dateB_str = b.metafield?.value || b.publishedAt;

          // Konvertera till Date-objekt. Om värde saknas helt, sätt ett väldigt gammalt datum.
          const dateA = dateA_str ? new Date(dateA_str) : new Date(0);
          const dateB = dateB_str ? new Date(dateB_str) : new Date(0);

          // Jämför datumen. dateB - dateA sorterar med det nyaste datumet först.
          comparison = dateB - dateA;
          break;
      }

      return direction === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }

  getSortPreferenceKey() {
    // Use collection ID or current path to make sort preferences page-specific
    const collectionId = this.collectionId || 'default';
    const path = window.location.pathname;
    return `variantSortPreference_${collectionId}_${path}`;
  }

  saveSortPreference() {
    try {
      const key = this.getSortPreferenceKey();
      localStorage.setItem(key, JSON.stringify(this.currentSort));
    } catch (e) {
      // Could not save sort preference
    }
  }

  loadSortPreference() {
    try {
      const key = this.getSortPreferenceKey();
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          this.currentSort = {
            type: parsed.type || null,
            direction: parsed.direction || null,
          };
        }
      }
    } catch (e) {
      this.currentSort = { type: null, direction: null };
    }
  }

  showLoadingState() {
    this.productGridContainer?.classList.add('loading');
  }

  hideLoadingState() {
    this.productGridContainer?.classList.remove('loading');
  }

  /**
   * Renders filter options dynamically based on the current product set.
   * @param {Array} products - The list of products to derive filters from.
   * @param {object} selectedFilters - The currently selected filters to preserve state.
   */
  renderDynamicFilters(products, selectedFilters) {
    const sizeSet = new Set();
    const brandSet = new Set();
    const typeSet = new Set();
    const ridingStyleSet = new Set();
    let hasSizeName = false;
    let hasLengthName = false;

    // --- Determine which BRANDS to show ---
    // Always filter for availability before using as filter source.
    // This ensures we only show brands that have products available in the currently selected sizes.
    const sourceForBrands = this.filterProductsInClient(products, {
      size: selectedFilters.size || [],
      type: selectedFilters.type || [],
      brand: [], // We ignore the brand filter on purpose here to get all available brands.
      q: selectedFilters.q || '',
    });
    sourceForBrands.forEach((product) => {
      brandSet.add(product.vendor);
    });

    // --- Determine which SIZES to show ---
    // Start with all products. If a brand is selected, filter the product pool first.
    const sourceForSizes = this.filterProductsInClient(products, {
      brand: selectedFilters.brand || [],
      type: selectedFilters.type || [],
      size: [],
      q: selectedFilters.q || '',
    });
    sourceForSizes.forEach((product) => {
      product.variants.nodes.forEach((variant) => {
        const matchedOption = variant.selectedOptions.find((opt) => this.isSizeLikeOptionName(opt.name));
        if (variant.availableForSale && matchedOption) {
          sizeSet.add(matchedOption.value);
          const optName = matchedOption.name.trim().toLowerCase();
          if (optName === 'size' || optName === 'storlek') hasSizeName = true;
          if (optName === 'length' || optName === 'längd') hasLengthName = true;
        }
      });
    });

    // --- Determine which TYPES to show ---
    const sourceForTypes = this.filterProductsInClient(products, {
      brand: selectedFilters.brand || [],
      size: selectedFilters.size || [],
      type: [],
      q: selectedFilters.q || '',
    });
    sourceForTypes.forEach((product) => {
      if (product.productType) {
        typeSet.add(this.canonicalizeProductType(product.productType));
      }
    });

    // --- Determine which RIDING STYLES to show ---
    const sourceForRidingStyles = this.filterProductsInClient(products, {
      brand: selectedFilters.brand || [],
      size: selectedFilters.size || [],
      type: selectedFilters.type || [],
      ridingStyle: [],
      q: selectedFilters.q || '',
    });
    sourceForRidingStyles.forEach((product) => {
      if (product.boardStyle && product.boardStyle.value) {
        try {
          const styles = JSON.parse(product.boardStyle.value);
          if (Array.isArray(styles)) {
            styles.forEach((style) => ridingStyleSet.add(style));
          }
        } catch (e) {
          // Invalid JSON, skip
        }
      }
    });

    this.renderFilterOptions('brand', Array.from(brandSet).sort(), selectedFilters);

    this.renderFilterOptions('size', Array.from(sizeSet), selectedFilters);
    this.renderFilterOptions('type', Array.from(typeSet).sort(), selectedFilters);
    this.renderFilterOptions('riding-style', Array.from(ridingStyleSet).sort(), selectedFilters);

    // Render boots size filter if enabled
    const bootsSizeFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="boots-size"]');
    if (bootsSizeFilterGroup && this.hasBootsSizeFilter) {
      this.renderBootsSizeFilter(products, selectedFilters);
    } else {
      selectedFilters.bootsSizeFilterEnabled = false;
      selectedFilters.bootsSizeFilter = [];
      selectedFilters.bootsSizeType = null;
    }

    // Update the size filter heading to reflect the detected option names
    const sizeGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="size"] h4');
    if (sizeGroup) {
      if (hasSizeName && hasLengthName) {
        sizeGroup.textContent = this.currentLocale === 'en' ? 'Size / Length' : 'Storlek / Längd';
      } else if (hasLengthName && !hasSizeName) {
        sizeGroup.textContent = this.currentLocale === 'en' ? 'Length' : 'Längd';
      } else {
        sizeGroup.textContent = this.currentLocale === 'en' ? 'Size' : 'Storlek';
      }
    }
  }

  /**
   * Renders the boots size filter with size type selector and size options.
   * @param {Array} products - The list of products to derive filters from.
   * @param {object} selectedFilters - The currently selected filters to preserve state.
   */
  renderBootsSizeFilter(products, selectedFilters) {
    // Get current boots size type from selected filters (URL) or state, default to 'euro'
    const currentSizeType = selectedFilters.bootsSizeType || this.bootsSizeType || 'euro';
    // Update state to match
    this.bootsSizeType = currentSizeType;
    selectedFilters.bootsSizeFilterEnabled = true;

    // Map size type to meta field key and display label
    const sizeTypeConfig = {
      euro: { key: 'euroSize', label: { en: 'Euro', sv: 'Euro' } },
      mondo: { key: 'mondoSize', label: { en: 'Mondo', sv: 'Mondo' } },
      us_mens: { key: 'usMens', label: { en: 'US Mens', sv: 'US Mens' } },
      us_womens: { key: 'usWomens', label: { en: 'US Womens', sv: 'US Womens' } },
      us_kids: { key: 'usKids', label: { en: 'US Kids', sv: 'US Kids' } },
    };

    // Render size type selector
    const sizeTypeGroup = this.filterWrapper.querySelector(
      '.filter-group[data-filter-group="boots-size"] .size__type_group'
    );
    if (sizeTypeGroup) {
      const sizeTypeWrapper = sizeTypeGroup.querySelector('.board-finder__swiper-wrapper');
      const sizeTypes = ['euro', 'mondo', 'us_mens', 'us_womens', 'us_kids'];

      if (sizeTypeWrapper) {
        // Always render as swiper slides (CSS handles desktop flex layout)
        sizeTypeWrapper.innerHTML = sizeTypes
          .map((type) => {
            const config = sizeTypeConfig[type];
            const label = config.label[this.currentLocale] || config.label.en;
            const activeClass = currentSizeType === type ? 'selected' : '';
            return `
              <div class="swiper-slide size__type ${activeClass}" data-size-type="${type}">
                <div class="board-style-slide"><span>${label}</span></div>
              </div>
            `;
          })
          .join('');

        // Initialize swiper for size type selector on mobile
        const sizeTypeSwiperContainer = sizeTypeGroup.querySelector('.board-finder__swiper');
        if (sizeTypeSwiperContainer && !this.bootsSizeTypeSwiper) {
          this.initializeSwiper(sizeTypeGroup, 'bootsSizeTypeSwiper');
        } else if (this.bootsSizeTypeSwiper) {
          this.bootsSizeTypeSwiper.update();
        }
      }
    }

    // Filter products by current size type to get available sizes
    const filteredProducts = this.filterProductsInClient(products, {
      ...selectedFilters,
      bootsSizeType: currentSizeType,
      bootsSizeFilter: [], // Don't filter by specific sizes yet, just by type
    });

    // Collect available sizes for the current size type
    const sizeSet = new Set();
    const metaFieldKey = sizeTypeConfig[currentSizeType]?.key;

    if (metaFieldKey) {
      filteredProducts.forEach((product) => {
        product.variants.nodes.forEach((variant) => {
          if (variant.availableForSale && variant[metaFieldKey] && variant[metaFieldKey].value) {
            // For decimal types (us_mens, us_womens), values come as strings like "6.5", "7.0"
            // For choice list types (us_kids), values come as strings like "7C", "8C", "1K"
            const rawValue = variant[metaFieldKey].value;
            sizeSet.add(rawValue);
          }
        });
      });
    }

    // Render size options
    const sizeOptionsContainer = this.filterWrapper.querySelector(
      '.filter-group[data-filter-group="boots-size"] #filter-boots-size-options'
    );
    if (sizeOptionsContainer) {
      const swiperWrapper = sizeOptionsContainer.querySelector('.swiper-wrapper');
      if (swiperWrapper) {
        const sizes = Array.from(sizeSet);
        const selectedSizes = new Set(selectedFilters.bootsSizeFilter || []);
        const uniqueMap = new Map();

        sizes.forEach((val) => {
          // Normalize based on size type
          const norm = this.normalizeBootsSize(val, currentSizeType);
          if (norm && !uniqueMap.has(norm)) {
            uniqueMap.set(norm, val);
          }
        });

        // Use boots-specific sorting for boots sizes
        const uniqueSorted = Array.from(uniqueMap.entries()).sort((a, b) =>
          this.bootsSizeSort(a[0], b[0], currentSizeType)
        );

        if (uniqueSorted.length === 0) {
          swiperWrapper.innerHTML = '<div class="swiper-slide">No options</div>';
        } else {
          swiperWrapper.innerHTML = uniqueSorted
            .map(([norm, label]) => {
              const display = this.formatBootsSize(norm, currentSizeType);
              const activeClass = selectedSizes.has(norm) ? 'selected' : '';
              return `
                <div class="swiper-slide ${activeClass}" data-original-value="${label}" data-normalized="${norm}">
                  <div class="board-style-slide"><span>${display}</span></div>
                </div>
              `;
            })
            .join('');
        }

        // Initialize or update swiper for size options
        if (!this.bootsSizeSwiper) {
          this.initializeSwiper(sizeOptionsContainer, 'bootsSizeSwiper');
        } else {
          this.bootsSizeSwiper.update();
        }
      }
    }
  }

  /**
   * Renders the actual filter options for a group.
   * @param {string} group - The name of the filter group (e.g., 'brand', 'size').
   * @param {Array<string>} values - The array of options for this filter.
   * @param {object} selectedFilters - The currently selected filters to preserve state.
   */
  renderFilterOptions(group, values, selectedFilters) {
    const container = document.getElementById(`filter-${group}-options`);
    if (!container) return;

    // Map group name to the correct key in selectedFilters
    const filterKey = group === 'riding-style' ? 'ridingStyle' : group;
    const selectedValues = new Set(selectedFilters[filterKey] || []);
    const swiperWrapper = container.querySelector('.swiper-wrapper');

    if (!swiperWrapper) {
      return;
    }

    if (group === 'size') {
      const uniqueMap = new Map();

      values.forEach((val) => {
        const norm = this.normalizeSize(val);
        if (norm && !uniqueMap.has(norm)) {
          uniqueMap.set(norm, val); // Use first-seen original label
        }
      });

      const uniqueSorted = Array.from(uniqueMap.entries()).sort((a, b) => this.customSizeSort(a[0], b[0]));

      if (uniqueSorted.length === 0) {
        swiperWrapper.innerHTML = '<div class="swiper-slide">No options</div>';
      } else {
        swiperWrapper.innerHTML = uniqueSorted
          .map(([norm, label]) => {
            const display = this.formatDisplaySize(norm);
            const activeClass = selectedValues.has(norm) ? 'selected' : '';
            return `
              <div class="swiper-slide ${activeClass}" data-original-value="${label}" data-normalized="${norm}">
                  <div class="board-style-slide"><span>${display}</span></div>
              </div>
          `;
          })
          .join('');
      }
    } else if (group === 'type') {
      if (values.length === 0) {
        swiperWrapper.innerHTML = '<div class="swiper-slide">No options</div>';
      } else {
        swiperWrapper.innerHTML = values
          .map((value) => {
            const activeClass = selectedValues.has(value) ? 'selected' : '';
            const translatedLabel = this.getDisplayProductType(value);
            return `
              <div class="swiper-slide ${activeClass}" data-original-value="${value}">
                  <div class="board-style-slide"><span>${translatedLabel}</span></div>
              </div>
          `;
          })
          .join('');
      }
    } else if (group === 'riding-style') {
      if (values.length === 0) {
        swiperWrapper.innerHTML = '<div class="swiper-slide">No options</div>';
      } else {
        // Map riding styles to translations
        const styleTranslations = {
          Carving: this.currentLocale === 'en' ? 'Carving' : 'Carving',
          Freestyle: this.currentLocale === 'en' ? 'Freestyle' : 'Freestyle',
          Puder: this.currentLocale === 'en' ? 'Powder' : 'Puder',
          'All Mountain': this.currentLocale === 'en' ? 'All Mountain' : 'All Mountain',
          Barn: this.currentLocale === 'en' ? 'Kids' : 'Barn',
        };
        swiperWrapper.innerHTML = values
          .map((value) => {
            const activeClass = selectedValues.has(value) ? 'selected' : '';
            const translatedLabel = styleTranslations[value] || value;
            return `
              <div class="swiper-slide ${activeClass}" data-original-value="${value}">
                  <div class="board-style-slide"><span>${translatedLabel}</span></div>
              </div>
          `;
          })
          .join('');
      }
    } else {
      if (values.length === 0) {
        swiperWrapper.innerHTML = '<div class="swiper-slide">No options</div>';
      } else {
        swiperWrapper.innerHTML = values
          .map((value) => {
            const activeClass = selectedValues.has(value) ? 'selected' : '';
            return `
                <div class="swiper-slide ${activeClass}" data-original-value="${value}">
                    <div class="board-style-slide"><span>${value}</span></div>
                </div>
            `;
          })
          .join('');
      }
    }

    const swiperInstanceName = group === 'riding-style' ? 'ridingStyleSwiper' : `${group}Swiper`; // e.g., 'sizeSwiper', 'brandSwiper', 'ridingStyleSwiper'

    if (this[swiperInstanceName]) {
      this[swiperInstanceName].update();
    } else {
      this.initializeSwiper(container, swiperInstanceName);
    }
  }

  initializeSwiper(container, swiperInstanceName) {
    if (window.innerWidth >= 990) return; // Skip Swiper on desktop

    const swiperEl = container.querySelector('.board-finder__swiper');
    if (swiperEl) {
      this[swiperInstanceName] = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 10,
        navigation: {
          nextEl: container.querySelector('.swiper-button-next'),
          prevEl: container.querySelector('.swiper-button-prev'),
        },
      });
    }
  }

  updateFilterURL(filters) {
    const params = new URLSearchParams();

    if (filters.size?.length) {
      params.set('size', filters.size.join(','));
    }
    if (filters.brand?.length) {
      params.set('brand', filters.brand.join(','));
    }
    if (filters.type?.length) {
      params.set('type', filters.type.join(','));
    }
    if (filters.ridingStyle?.length) {
      params.set('ridingStyle', filters.ridingStyle.join(','));
    }
    if (filters.q && filters.q.length) {
      params.set('q', filters.q);
    }
    if (filters.riderLengthEnabled) {
      params.set('riderLength', filters.riderLength);
    }
    if (filters.bootSizeEnabled) {
      params.set('bootSize', filters.bootSize);
    }
    if (filters.deckWidthEnabled) {
      params.set('deckWidth', filters.deckWidth);
    }
    if (filters.bootsSizeFilterEnabled && filters.bootsSizeType) {
      params.set('bootsSizeType', filters.bootsSizeType);
    }
    if (filters.bootsSizeFilterEnabled && filters.bootsSizeFilter?.length) {
      params.set('bootsSizeFilter', filters.bootsSizeFilter.join(','));
    }

    const queryString = params.toString();
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

    window.history.pushState({ path: newUrl }, '', newUrl);
  }

  getFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);
    const size = params.get('size')?.split(',') || [];
    const brand = params.get('brand')?.split(',') || [];
    const type = params.get('type')?.split(',') || [];
    const ridingStyle = params.get('ridingStyle')?.split(',') || [];
    const q = params.get('q') || '';
    const riderLength = params.get('riderLength') ? parseFloat(params.get('riderLength')) : 170;
    const bootSize = params.get('bootSize') ? parseFloat(params.get('bootSize')) : 42;
    const deckWidth = params.get('deckWidth') ? parseFloat(params.get('deckWidth')) : 8.0;
    const riderLengthEnabled = params.has('riderLength');
    const bootSizeEnabled = params.has('bootSize');
    const hasDeckWidthFilter = !!document.getElementById('enable-deck-width');
    const deckWidthEnabled = params.has('deckWidth') || (hasDeckWidthFilter && !params.toString());
    const bootsSizeTypeParam = params.get('bootsSizeType');
    const bootsSizeFilterParam = params.get('bootsSizeFilter')?.split(',').filter(Boolean) || [];
    const bootsSizeFilterEnabled = this.hasBootsSizeFilter;

    return {
      size: size.filter(Boolean),
      brand: brand.filter(Boolean),
      type: type.filter(Boolean),
      ridingStyle: ridingStyle.filter(Boolean),
      q,
      riderLength,
      bootSize,
      deckWidth,
      riderLengthEnabled,
      bootSizeEnabled,
      deckWidthEnabled,
      bootsSizeType: bootsSizeFilterEnabled ? bootsSizeTypeParam || this.bootsSizeType || 'euro' : null,
      bootsSizeFilter: bootsSizeFilterEnabled ? bootsSizeFilterParam : [],
      bootsSizeFilterEnabled,
    };
  }

  forceApplyFilterUIState(filters) {
    ['brand', 'size', 'type', 'riding-style'].forEach((group) => {
      const filterKey = group === 'riding-style' ? 'ridingStyle' : group;
      const values = new Set(filters[filterKey] || []);
      this.filterWrapper
        .querySelectorAll(`.filter-group[data-filter-group="${group}"] .swiper-slide`)
        .forEach((slide) => {
          const val = group === 'size' ? slide.dataset.normalized : slide.dataset.originalValue;

          if (val && values.has(val)) {
            slide.classList.add('selected');
          } else {
            slide.classList.remove('selected');
          }
        });
    });

    // Populate search UI
    const searchInput = this.filterWrapper.querySelector('#variant-filter-search');
    const clearBtn = this.filterWrapper.querySelector('.filter-options--search .field__clear');
    if (searchInput) {
      searchInput.value = filters.q || '';
      if (clearBtn) clearBtn.hidden = !(filters.q && filters.q.length);
    }

    // Set slider values and checkbox states
    const riderLengthSlider = document.getElementById('rider-length-slider');
    const riderLengthCheckbox = document.getElementById('enable-rider-length');
    const riderLengthSelect = document.getElementById('rider-length-select');
    const riderLengthFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="rider-length"]');
    if (riderLengthSlider && riderLengthCheckbox) {
      riderLengthSlider.value = filters.riderLength || 170;
      riderLengthCheckbox.checked = filters.riderLengthEnabled || false;
      riderLengthSlider.disabled = !riderLengthCheckbox.checked;
      if (riderLengthSelect) {
        riderLengthSelect.value = riderLengthSlider.value;
      }
      this.riderLength = parseFloat(riderLengthSlider.value);
      this.riderLengthEnabled = riderLengthCheckbox.checked;
      // Update filter group class based on checkbox state
      if (riderLengthFilterGroup) {
        if (riderLengthCheckbox.checked) {
          riderLengthFilterGroup.classList.add('filter-active');
        } else {
          riderLengthFilterGroup.classList.remove('filter-active');
        }
      }
    }

    const bootSizeSlider = document.getElementById('boot-size-slider');
    const bootSizeCheckbox = document.getElementById('enable-boot-size');
    const bootSizeSelect = document.getElementById('boot-size-select');
    const bootSizeFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="boot-size"]');
    if (bootSizeSlider && bootSizeCheckbox) {
      bootSizeSlider.value = filters.bootSize || 42;
      bootSizeCheckbox.checked = filters.bootSizeEnabled || false;
      bootSizeSlider.disabled = !bootSizeCheckbox.checked;
      if (bootSizeSelect) {
        bootSizeSelect.value = bootSizeSlider.value;
      }
      this.bootSize = parseFloat(bootSizeSlider.value);
      this.bootSizeEnabled = bootSizeCheckbox.checked;
      // Update filter group class based on checkbox state
      if (bootSizeFilterGroup) {
        if (bootSizeCheckbox.checked) {
          bootSizeFilterGroup.classList.add('filter-active');
        } else {
          bootSizeFilterGroup.classList.remove('filter-active');
        }
      }
    }

    const deckWidthSlider = document.getElementById('deck-width-slider');
    const deckWidthCheckbox = document.getElementById('enable-deck-width');
    const deckWidthSelect = document.getElementById('deck-width-select');
    const deckWidthFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="deck-width"]');
    if (deckWidthSlider && deckWidthCheckbox) {
      deckWidthSlider.value = filters.deckWidth || 8.0;
      deckWidthCheckbox.checked = filters.deckWidthEnabled || false;
      deckWidthSlider.disabled = !deckWidthCheckbox.checked;
      if (deckWidthSelect) {
        deckWidthSelect.value = deckWidthSlider.value;
      }
      this.deckWidth = parseFloat(deckWidthSlider.value);
      this.deckWidthEnabled = deckWidthCheckbox.checked;
      if (deckWidthFilterGroup) {
        if (deckWidthCheckbox.checked) {
          deckWidthFilterGroup.classList.add('filter-active');
        } else {
          deckWidthFilterGroup.classList.remove('filter-active');
        }
      }
    }

    // Set boots size filter state
    if (filters.bootsSizeType) {
      this.bootsSizeType = filters.bootsSizeType;
    }
    if (filters.bootsSizeFilter) {
      this.bootsSizeFilter = filters.bootsSizeFilter;
    }

    // Apply boots size type selection UI
    const bootsSizeFilterGroup = this.filterWrapper.querySelector('.filter-group[data-filter-group="boots-size"]');
    if (bootsSizeFilterGroup && filters.bootsSizeFilterEnabled && filters.bootsSizeType) {
      bootsSizeFilterGroup.querySelectorAll('[data-size-type]').forEach((el) => {
        el.classList.remove('selected', 'selected__sizerange');
        if (el.dataset.sizeType === filters.bootsSizeType) {
          el.classList.add(el.classList.contains('swiper-slide') ? 'selected' : 'selected__sizerange');
        }
      });
    } else if (bootsSizeFilterGroup && !filters.bootsSizeFilterEnabled) {
      bootsSizeFilterGroup.querySelectorAll('[data-size-type]').forEach((el) => {
        el.classList.remove('selected', 'selected__sizerange');
      });
    }

    // Apply boots size selection UI
    if (bootsSizeFilterGroup && filters.bootsSizeFilterEnabled && filters.bootsSizeFilter) {
      const selectedSizes = new Set(filters.bootsSizeFilter);
      bootsSizeFilterGroup.querySelectorAll('#filter-boots-size-options .swiper-slide').forEach((slide) => {
        const norm = slide.dataset.normalized;
        if (norm && selectedSizes.has(norm)) {
          slide.classList.add('selected');
        } else {
          slide.classList.remove('selected');
        }
      });
    } else if (bootsSizeFilterGroup && !filters.bootsSizeFilterEnabled) {
      bootsSizeFilterGroup
        .querySelectorAll('#filter-boots-size-options .swiper-slide')
        .forEach((slide) => slide.classList.remove('selected'));
    }
  }

  normalizeSize(value) {
    if (!value) return '';

    let normalized = value.toUpperCase().replace(/\s+/g, '');

    // Convert .5 to "5" for internal matching
    normalized = normalized
      .replace(/^US(\d+)\.5$/, 'US$15') // US9.5 → US95
      .replace(/^W-US(\d+)\.5$/, 'W-US$15') // W-US6.5 → W-US65
      .replace(/^(\d+)\.5\/(\d+)([CK])$/, '$15/$2$3'); // 36.5/5C → 365/5C

    // Convert all half sizes with .5 at the end
    normalized = normalized.replace(/(\d)\.5$/, '$15'); // fallback for any remaining

    // Normalize Kids sizes: treat C and K as equivalent → always use /K
    normalized = normalized.replace(/\/(\d+)[CK]$/, '/$1K');

    return normalized;
  }

  formatDisplaySize(normalizedValue) {
    if (!normalizedValue) return '';

    let formatted = normalizedValue;

    // Revert .5 for US sizes (e.g., US105 -> US10.5)
    formatted = formatted.replace(/^(US|W-US)(\d+)5$/, '$1$2.5');

    // Handle specific kids half-size case: 365/5K → 36.5 / 5K
    formatted = formatted.replace(/^365\/(\d+K)$/, '36.5 / $1');

    // Add spacing for other kids' sizes (e.g., 38/6K → 38 / 6K)
    if (!formatted.includes('.')) {
      formatted = formatted.replace(/^(\d+)\/(\d+K)$/, '$1 / $2');
    }

    return formatted;
  }

  /**
   * Normalizes boots sizes for consistent comparison.
   * @param {string} value - The raw size value from metafield
   * @param {string} sizeType - The current boots size type
   * @returns {string} - Normalized value for comparison
   */
  normalizeBootsSize(value, sizeType) {
    if (!value) return '';

    // For decimal types (us_mens, us_womens), values come as "6.5", "7.0", "7.5"
    // Normalize by converting to consistent decimal format
    if (sizeType === 'us_mens' || sizeType === 'us_womens') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        // Return as string with one decimal place for consistency
        return num.toFixed(1);
      }
      return value;
    }

    // For choice list types (us_kids), values come as "7C", "8C", "1K", "2K"
    // Keep as-is for comparison
    if (sizeType === 'us_kids') {
      return value.toUpperCase().trim();
    }

    // For other types (euro, mondo), normalize as string
    return String(value).trim();
  }

  /**
   * Sorts boots sizes according to size type.
   * @param {string} a - First normalized size
   * @param {string} b - Second normalized size
   * @param {string} sizeType - The current boots size type (euro, mondo, us_mens, us_womens, us_kids)
   * @returns {number} - Sort order
   */
  bootsSizeSort(a, b, sizeType) {
    if (sizeType === 'us_mens' || sizeType === 'us_womens') {
      // US Mens/Womens: decimal values like "6.5", "7.0", "7.5", "10.0", "10.5"
      // Sort numerically
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return String(a).localeCompare(String(b));
    }

    if (sizeType === 'us_kids') {
      // Kids: choice list values like "7C", "8C", "9C", "10C", "11C", "12C", "13C", "1K", "2K", "3K", "4K", "5K", "6K", "7K"
      const extractKids = (val) => {
        // Handle formats like "7C", "8C", "1K", "2K", etc.
        const cMatch = val.match(/^(\d+)C$/i);
        if (cMatch) {
          return { type: 'C', num: parseInt(cMatch[1], 10) };
        }
        const kMatch = val.match(/^(\d+)K$/i);
        if (kMatch) {
          return { type: 'K', num: parseInt(kMatch[1], 10) };
        }
        return { type: 'Z', num: 0 };
      };
      const aVal = extractKids(a);
      const bVal = extractKids(b);
      // C comes before K
      if (aVal.type !== bVal.type) {
        return aVal.type === 'C' ? -1 : aVal.type === 'K' ? 1 : 0;
      }
      return aVal.num - bVal.num;
    }

    // For other size types (euro, mondo), use numeric sorting
    const extractNumeric = (val) => {
      const match = val.match(/(\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : 0;
    };
    return extractNumeric(a) - extractNumeric(b);
  }

  /**
   * Formats boots sizes for display based on size type.
   * @param {string} normalizedValue - The normalized size value
   * @param {string} sizeType - The current boots size type
   * @returns {string} - Formatted display string
   */
  formatBootsSize(normalizedValue, sizeType) {
    if (!normalizedValue) return '';

    if (sizeType === 'euro') {
      // EURO: decimal values like "35", "35.5", "36", "36.5"
      // Format: "EU 35", "EU 35.5", "EU 36", etc.
      const num = parseFloat(normalizedValue);
      if (!isNaN(num)) {
        // If it's a whole number, display without decimal (35.0 -> 35)
        if (num % 1 === 0) {
          return `EU ${num}`;
        }
        // Otherwise display with one decimal (35.5 -> 35.5)
        return `EU ${num.toFixed(1)}`;
      }
      return `EU ${normalizedValue}`;
    }

    if (sizeType === 'us_mens') {
      // US Mens: decimal values like "6.5", "7.0", "7.5", "10.0", "10.5"
      // Format: "US 7", "US 7.5", "US 8", etc.
      const num = parseFloat(normalizedValue);
      if (!isNaN(num)) {
        // If it's a whole number, display without decimal (7.0 -> 7)
        if (num % 1 === 0) {
          return `US ${num}`;
        }
        // Otherwise display with one decimal (6.5 -> 6.5, 7.5 -> 7.5)
        return `US ${num.toFixed(1)}`;
      }
      return `US ${normalizedValue}`;
    }

    if (sizeType === 'us_womens') {
      // US Womens: decimal values like "5.0", "5.5", "6.0", "6.5"
      // Format: "W-US 5", "W-US 5.5", "W-US 6", etc.
      const num = parseFloat(normalizedValue);
      if (!isNaN(num)) {
        // If it's a whole number, display without decimal (5.0 -> 5)
        if (num % 1 === 0) {
          return `W-US ${num}`;
        }
        // Otherwise display with one decimal (5.5 -> 5.5, 6.5 -> 6.5)
        return `W-US ${num.toFixed(1)}`;
      }
      return `W-US ${normalizedValue}`;
    }

    if (sizeType === 'us_kids') {
      // Kids: choice list values like "7C", "8C", "1K", "2K"
      // Display as-is (no prefix)
      return normalizedValue;
    }

    // For other types (mondo), use the standard format
    return this.formatDisplaySize(normalizedValue);
  }
}

// Instantiate the class only after the DOM is fully loaded to avoid timing issues.
document.addEventListener('DOMContentLoaded', () => {
  new VariantFilter();
});
